import { Button } from '@/components/ui/button.jsx'
import { Icon } from '@iconify/react'
import { useDashboardContext } from '@/context-state/DashboardContext.jsx'
import { CreativePreview } from '@/pages/dashboard/library/creative/CreativePreview.jsx'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { handleError } from '@/services/errorHandler.js'
import { createFolder } from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { useNavigate, useParams } from 'react-router-dom'
import { AdAccountSelect } from '@/components/ad-account-select/AdAccountSelect.jsx'
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent
} from '@/components/ui/accordion.jsx'
import { GradientText } from '@/components/gradient/index.jsx'
import { useLibraryContext } from '@/context-state/LibraryContext.jsx'

export const ClipsLibrarySubNavbar = () => {
    const { creativeContext, adAccountsContext } = useDashboardContext()
    const { updateFolderState, getAllFoldersFromState, getCreativesOfFolderFromState } = creativeContext
    const { chosenAdAccount } = adAccountsContext
    const [creativeToPreview, setCreativeToPreview] = useState(null)
    const topLevelFolders = getAllFoldersFromState().filter(folder => !folder.parent)
    const { folderId } = useParams()
    const navigate = useNavigate()

    // Use LibraryContext for tags and boards
    const {
        getAllTags,
        getAiSuggestedTags,
        getAllBoards,
        addTag,
        addBoard,
        selectedTag,
        activeFilter,
        selectedBoard,
        handleTagSelect,
        handleFilterSelect,
        handleBoardSelect
    } = useLibraryContext()

    // Get tags and boards from context
    const tags = getAllTags()
    const aiSuggestedTags = getAiSuggestedTags()
    const boards = getAllBoards()

    // Handle creating a new board
    const handleCreateBoard = () => {
        // Logic to create a new board
        const boardName = prompt('Enter board name')
        if (boardName) {
            addBoard(boardName)
        } else {
            toast.info('Board creation canceled')
        }
    }

    // Handle board selection and navigation
    const handleBoardSelection = (board) => {
        handleBoardSelect(board)
        navigate(`clips-library/board/${board.id}`)
    }

    const navigateToLibraryRoot = () => {
        const currentPath = window.location.pathname // Use `useLocation` to get this
        if (currentPath.endsWith('clips-library')) {
            return
        }
        const newPath = currentPath.split('/').slice(0, -1).join('/') // Remove last path name which will be the folderId - '/library/:folderId' and navigate back to '/library'
        navigate(newPath)
    }

    return (
        <div className="p-4">
            <div>
                <AdAccountSelect />

                {/* Filter buttons */}
                <div className="flex gap-2 mb-4 mt-2">
                    <Button
                        variant={activeFilter === 'all' && !selectedTag ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 font-normal"
                        onClick={() => {
                            handleFilterSelect('all')
                            navigate('clips-library')
                        }}
                    >
                        <Icon icon="lucide:layers" className="h-4 w-4 mr-2" />
                        All Clips
                    </Button>
                    <Button
                        variant={activeFilter === 'untagged' ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1 font-normal"
                        onClick={() => {
                            handleFilterSelect('untagged')
                            navigate('clips-library')
                        }}
                    >
                        <Icon icon="lucide:tag-off" className="h-4 w-4 mr-2" />
                        Untagged
                    </Button>
                </div>

                {/* Tags Accordion with both regular and AI suggested tags */}
                <div className="mb-4">
                    <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="tags" className="border-0">
                            <AccordionTrigger className="py-2 px-2 hover:bg-accent rounded-md">
                                <div className="flex items-center gap-2">
                                    <Icon icon="lucide:tag" className="h-4 w-4" />
                                    <span>Clips By Tags</span>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-1 pl-6">
                                    {/* Regular tags */}
                                    {
                                        tags.map((tag) => {
                                            return (
                                                <div
                                                    key={tag.id}
                                                    className={`flex items-center gap-2 p-2 text-sm rounded-md cursor-pointer hover:bg-accent transition-colors ${selectedTag?.id === tag.id ? 'bg-accent' : ''}`}
                                                    onClick={() => {
                                                        handleTagSelect(tag)
                                                        navigate('clips-library')
                                                    }}
                                                >
                                                    <Icon
                                                        icon="lucide:tag"
                                                        className="h-3.5 w-3.5"
                                                    />
                                                    <TypographySmall className="font-light">
                                                        {tag.name}
                                                    </TypographySmall>
                                                </div>
                                            )
                                        })
                                    }

                                    {/* Separator with AI Suggested Tags label */}
                                    <div className="pt-2 pb-1">
                                        <div className="flex items-center gap-2">
                                            <div className="h-px bg-gray-300 flex-grow"></div>
                                            <div className="flex items-center gap-1">
                                                <Icon
                                                    icon="solar:magic-stick-linear"
                                                    className="h-3.5 w-3.5"
                                                />
                                                <GradientText
                                                    colors={["#4F46E5", "#EC4899", "#8B5CF6"]}
                                                    animationSpeed={6}
                                                    className="text-xs font-medium"
                                                >
                                                    AI Suggestions
                                                </GradientText>
                                            </div>
                                            <div className="h-px bg-gray-300 flex-grow"></div>
                                        </div>
                                    </div>

                                    {/* AI suggested tags */}
                                    {
                                        aiSuggestedTags.map((tag) => {
                                            return (
                                                <div
                                                    key={tag.id}
                                                    className={`flex items-center gap-2 p-2 text-sm rounded-md cursor-pointer hover:bg-accent transition-colors ${selectedTag?.id === tag.id ? 'bg-accent' : ''}`}
                                                    onClick={() => {
                                                        handleTagSelect(tag)
                                                        navigate('clips-library')
                                                    }}
                                                >
                                                    <Icon
                                                        icon="solar:magic-stick-linear"
                                                        className="h-3.5 w-3.5"
                                                    />
                                                    <TypographySmall className="font-light">
                                                        <GradientText
                                                            colors={["#4F46E5", "#EC4899", "#8B5CF6"]}
                                                            animationSpeed={6}
                                                        >
                                                            {tag.name}
                                                        </GradientText>
                                                    </TypographySmall>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>

                {/* Boards Accordion */}
                <div className="mb-4">
                    <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="boards" className="border-0">
                            <AccordionTrigger className="py-2 px-2 hover:bg-accent rounded-md">
                                <div className="flex items-center gap-2">
                                    <Icon icon="lucide:layout-grid" className="h-4 w-4" />
                                    <span>Boards</span>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-1 pl-6">
                                    {/* Add Board Button */}
                                    <div
                                        className="flex items-center gap-2 p-2 text-sm rounded-md cursor-pointer hover:bg-accent transition-colors text-primary"
                                        onClick={handleCreateBoard}
                                    >
                                        <Icon
                                            icon="lucide:plus-circle"
                                            className="h-3.5 w-3.5"
                                        />
                                        <TypographySmall className="font-medium">
                                            Add Board
                                        </TypographySmall>
                                    </div>

                                    {/* Separator */}
                                    <div className="h-px bg-gray-300 my-2"></div>

                                    {/* Boards list */}
                                    {
                                        boards.map((board) => {
                                            return (
                                                <div
                                                    key={board.id}
                                                    className={`flex items-center justify-between p-2 text-sm rounded-md cursor-pointer hover:bg-accent transition-colors ${selectedBoard?.id === board.id ? 'bg-accent' : ''}`}
                                                    onClick={() => handleBoardSelection(board)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Icon
                                                            icon="lucide:layout-grid"
                                                            className="h-3.5 w-3.5"
                                                        />
                                                        <TypographySmall className="font-light">
                                                            {board.name}
                                                        </TypographySmall>
                                                    </div>
                                                    <TypographySmall className="text-muted-foreground">
                                                        {board.clipCount}
                                                    </TypographySmall>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </div>
    )
}